import { Capacitor } from '@capacitor/core';

/**
 * Desktop notifications for the incoming-call card (the browser's
 * Notification API). Asked for only when the person presses "Turn on desktop
 * notifications" on /sales, never on page load: a browser that is asked
 * unprompted tends to be told no for good.
 *
 * The app on a phone has none of this (a web view has no Notification API);
 * the in-app card still shows there.
 */

const PREF_KEY = 'sales_desktop_notifications';

export function desktopNotifySupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && !Capacitor.isNativePlatform();
}

export type DesktopNotifyState = 'unsupported' | 'off' | 'on' | 'blocked';

export function desktopNotifyState(): DesktopNotifyState {
  if (!desktopNotifySupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'blocked';
  let pref: string | null = null;
  try { pref = localStorage.getItem(PREF_KEY); } catch { /* private window */ }
  return Notification.permission === 'granted' && pref !== 'off' ? 'on' : 'off';
}

/** Asks the browser, and remembers the choice on this device. */
export async function turnOnDesktopNotify(): Promise<DesktopNotifyState> {
  if (!desktopNotifySupported()) return 'unsupported';
  const answer = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
  try { localStorage.setItem(PREF_KEY, answer === 'granted' ? 'on' : 'off'); } catch { /* private window */ }
  return desktopNotifyState();
}

export function turnOffDesktopNotify(): DesktopNotifyState {
  try { localStorage.setItem(PREF_KEY, 'off'); } catch { /* private window */ }
  return desktopNotifyState();
}

/** Shows one, if switched on. `tag` replaces an earlier one for the same call. */
export function desktopNotify(title: string, body: string, tag: string, onClick?: () => void): Notification | null {
  if (desktopNotifyState() !== 'on') return null;
  try {
    const n = new Notification(title, { body, tag });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
    return n;
  } catch {
    return null;
  }
}
